import React, { useState, useEffect } from 'react'
import { Row, Col, Form } from 'react-bootstrap'
import {
  Organization,
  useGetOrganizationBrandingPublicQuery,
  useGetOrganizationPublicQuery,
  useUpdateOrganizationMutation,
  useUploadOrganizationBrandingMutation,
  UpdateOrganizationReq,
} from '../../../../services/orgsApi'
import { useGetUsersQuery } from '../../../../services/userApi'
import { User } from '../../../../types/models/user'
import { AdminCard, PrimaryButton, EditButton } from '../styles'
import styled from 'styled-components'
import {
  FaPencilAlt,
  FaBuilding,
  FaInfoCircle,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaCalendar,
} from 'react-icons/fa'

const LogoImage = styled.img`
  height: 80px;
  width: auto;
  max-width: 250px;
  object-fit: contain;
  filter: drop-shadow(0 2px 8px rgba(34, 211, 238, 0.25));
`

const SectionCard = styled(AdminCard)`
  margin-bottom: 1.5rem;
`

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`

const InfoItem = styled.div`
  padding: 1.25rem;
  background: rgba(34, 211, 238, 0.05);
  border: 1px solid rgba(34, 211, 238, 0.15);
  border-radius: 12px;
  transition: all 0.25s ease;

  &:hover {
    background: rgba(34, 211, 238, 0.08);
    border-color: rgba(34, 211, 238, 0.25);
    transform: translateY(-2px);
  }

  .info-label {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.875rem;
    font-weight: 500;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .info-value {
    color: #ffffff;
    font-size: 1rem;
    font-weight: 600;
  }

  .info-secondary {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.875rem;
    margin-top: 0.25rem;
  }
`

const StyledFormControl = styled(Form.Control)`
  background: rgba(34, 211, 238, 0.05);
  border: 1px solid rgba(34, 211, 238, 0.2);
  color: #ffffff;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  transition: all 0.25s ease;

  &:focus {
    background: rgba(34, 211, 238, 0.1);
    border-color: rgba(34, 211, 238, 0.4);
    color: #ffffff;
    box-shadow: 0 0 0 3px rgba(34, 211, 238, 0.2);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`

const StyledSelect = styled(Form.Select)`
  background: rgba(34, 211, 238, 0.05);
  border: 1px solid rgba(34, 211, 238, 0.2);
  color: #ffffff;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  transition: all 0.25s ease;

  &:focus {
    background: rgba(34, 211, 238, 0.1);
    border-color: rgba(34, 211, 238, 0.4);
    color: #ffffff;
    box-shadow: 0 0 0 3px rgba(34, 211, 238, 0.2);
  }

  option {
    background: rgba(26, 26, 26, 0.95);
    color: #ffffff;
  }
`

const LogoContainer = styled.div`
  cursor: pointer;
  padding: 1.5rem;
  border: 2px dashed rgba(34, 211, 238, 0.3);
  border-radius: 12px;
  background: rgba(34, 211, 238, 0.03);
  transition: all 0.25s ease;
  text-align: center;

  &:hover {
    border-color: rgba(34, 211, 238, 0.5);
    background: rgba(34, 211, 238, 0.08);
  }
`

const HiddenFileInput = styled.input`
  display: none;
`

const OrganizationTab: React.FC<{ organization: Organization }> = ({ organization }) => {
  const { data: brandingBlob, refetch: refetchBranding } = useGetOrganizationBrandingPublicQuery()
  const { refetch: refetchOrganization } = useGetOrganizationPublicQuery()
  const { data: usersData } = useGetUsersQuery({ limit: 1000 })
  const [updateOrganization] = useUpdateOrganizationMutation()
  const [uploadBranding] = useUploadOrganizationBrandingMutation()

  const [logoUrl, setLogoUrl] = useState<string | undefined>()
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState<UpdateOrganizationReq>({
    name: organization.name || '',
    primary_poc: organization.primary_poc || '',
    secondary_poc: organization.secondary_poc || '',
    primary_phone: organization.primary_phone || '',
    secondary_phone: organization.secondary_phone || '',
  })
  const [brandingFile, setBrandingFile] = useState<File | null>(null)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const organizationUsers =
    usersData?.users?.filter((user: User) => user.org_id === organization.id) || []

  const primaryUser = organizationUsers.find((user: User) => user.id === organization.primary_poc)
  const secondaryUser = organizationUsers.find(
    (user: User) => user.id === organization.secondary_poc
  )

  useEffect(() => {
    if (brandingBlob) {
      const url = URL.createObjectURL(brandingBlob)
      setLogoUrl(url)
      return () => {
        URL.revokeObjectURL(url)
      }
    } else {
      setLogoUrl(undefined)
    }
  }, [brandingBlob])

  useEffect(() => {
    if (organization) {
      setFormData({
        name: organization.name || '',
        primary_poc: organization.primary_poc || '',
        secondary_poc: organization.secondary_poc || '',
        primary_phone: organization.primary_phone || '',
        secondary_phone: organization.secondary_phone || '',
      })
    }
  }, [organization])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setBrandingFile(file)
      const url = URL.createObjectURL(file)
      setLogoUrl(url)
    }
  }

  const handleSave = async () => {
    setIsLoading(true)
    try {
      if (brandingFile) {
        await uploadBranding(brandingFile).unwrap()
        await refetchBranding()
        setBrandingFile(null)
      }
      await updateOrganization(formData).unwrap()
      await refetchOrganization()
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to update organization:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    setFormData({
      name: organization.name || '',
      primary_poc: organization.primary_poc || '',
      secondary_poc: organization.secondary_poc || '',
      primary_phone: organization.primary_phone || '',
      secondary_phone: organization.secondary_phone || '',
    })
    setBrandingFile(null)
    if (brandingBlob) {
      const url = URL.createObjectURL(brandingBlob)
      setLogoUrl(url)
    }
    setIsEditing(false)
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <>
      <Row>
        <Col md={4}>
          <SectionCard>
            <div
              className="card-header"
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <h5
                className="mb-0"
                style={{ color: '#ffffff', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <FaBuilding size={18} />
                Organization Logo
              </h5>
            </div>
            <div className="card-body">
              <LogoContainer
                onClick={() => isEditing && fileInputRef.current?.click()}
                style={{ cursor: isEditing ? 'pointer' : 'default' }}
              >
                {logoUrl ? (
                  <LogoImage src={logoUrl} alt={`${organization.name} Logo`} />
                ) : (
                  <div
                    style={{
                      height: '120px',
                      width: '120px',
                      backgroundColor: 'rgba(34, 211, 238, 0.1)',
                      borderRadius: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid rgba(34, 211, 238, 0.25)',
                      color: '#22d3ee',
                      margin: '0 auto',
                    }}
                  >
                    <FaBuilding size={48} />
                  </div>
                )}
                {isEditing && (
                  <div
                    style={{
                      marginTop: '1rem',
                      color: 'rgba(255, 255, 255, 0.6)',
                      fontSize: '0.875rem',
                    }}
                  >
                    Click to upload new logo
                  </div>
                )}
              </LogoContainer>
              <HiddenFileInput
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
              />
            </div>
          </SectionCard>
        </Col>
        <Col md={8}>
          <SectionCard>
            <div
              className="card-header"
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <h5
                className="mb-0"
                style={{ color: '#ffffff', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <FaInfoCircle size={18} />
                Organization Information
              </h5>
              {!isEditing ? (
                <EditButton onClick={() => setIsEditing(true)} title="Edit Organization">
                  <FaPencilAlt size={16} />
                </EditButton>
              ) : (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <PrimaryButton
                    onClick={handleCancel}
                    style={{
                      padding: '0.5rem 1rem',
                      fontSize: '0.875rem',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                    }}
                  >
                    Cancel
                  </PrimaryButton>
                  <PrimaryButton onClick={handleSave} disabled={isLoading}>
                    {isLoading ? 'Saving...' : 'Save'}
                  </PrimaryButton>
                </div>
              )}
            </div>
            <div className="card-body">
              {!isEditing ? (
                <InfoGrid>
                  <InfoItem>
                    <div className="info-label">
                      <FaBuilding size={14} />
                      Organization Name
                    </div>
                    <div className="info-value">{organization.name}</div>
                  </InfoItem>
                  <InfoItem>
                    <div className="info-label">Organization ID</div>
                    <div
                      className="info-value"
                      style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                    >
                      {organization.id}
                    </div>
                  </InfoItem>
                  <InfoItem>
                    <div className="info-label">
                      <FaUser size={14} />
                      Primary Point of Contact
                    </div>
                    <div className="info-value">
                      {primaryUser
                        ? `${primaryUser.first_name} ${primaryUser.last_name}`
                        : 'Not set'}
                    </div>
                    {primaryUser && (
                      <div
                        className="info-secondary"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          marginTop: '0.5rem',
                        }}
                      >
                        <FaEnvelope size={12} />
                        {primaryUser.email}
                      </div>
                    )}
                  </InfoItem>
                  <InfoItem>
                    <div className="info-label">
                      <FaUser size={14} />
                      Secondary Point of Contact
                    </div>
                    <div className="info-value">
                      {secondaryUser
                        ? `${secondaryUser.first_name} ${secondaryUser.last_name}`
                        : 'Not set'}
                    </div>
                    {secondaryUser && (
                      <div
                        className="info-secondary"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          marginTop: '0.5rem',
                        }}
                      >
                        <FaEnvelope size={12} />
                        {secondaryUser.email}
                      </div>
                    )}
                  </InfoItem>
                  {organization.primary_phone && (
                    <InfoItem>
                      <div className="info-label">
                        <FaPhone size={14} />
                        Primary Phone
                      </div>
                      <div className="info-value">{organization.primary_phone}</div>
                    </InfoItem>
                  )}
                  {organization.secondary_phone && (
                    <InfoItem>
                      <div className="info-label">
                        <FaPhone size={14} />
                        Secondary Phone
                      </div>
                      <div className="info-value">{organization.secondary_phone}</div>
                    </InfoItem>
                  )}
                  <InfoItem>
                    <div className="info-label">
                      <FaCalendar size={14} />
                      Created At
                    </div>
                    <div className="info-value" style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                      {formatDate(organization.created_at)}
                    </div>
                  </InfoItem>
                  <InfoItem>
                    <div className="info-label">
                      <FaCalendar size={14} />
                      Updated At
                    </div>
                    <div className="info-value" style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                      {formatDate(organization.updated_at)}
                    </div>
                  </InfoItem>
                </InfoGrid>
              ) : (
                <Form>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                          Organization Name
                        </Form.Label>
                        <StyledFormControl
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Enter organization name"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                          Organization ID
                        </Form.Label>
                        <StyledFormControl
                          type="text"
                          value={organization.id}
                          disabled
                          style={{ opacity: 0.6 }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                          Primary Point of Contact
                        </Form.Label>
                        <StyledSelect
                          value={formData.primary_poc || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              primary_poc: e.target.value || undefined,
                            })
                          }
                        >
                          <option value="">Select Primary POC</option>
                          {organizationUsers.map((user: User) => (
                            <option key={user.id} value={user.id}>
                              {user.first_name} {user.last_name} ({user.username})
                            </option>
                          ))}
                        </StyledSelect>
                        {formData.primary_poc && (
                          <div
                            style={{
                              marginTop: '0.5rem',
                              color: 'rgba(255, 255, 255, 0.7)',
                              fontSize: '0.875rem',
                            }}
                          >
                            <FaEnvelope size={12} style={{ marginRight: '0.5rem' }} />
                            {
                              organizationUsers.find((u: User) => u.id === formData.primary_poc)
                                ?.email
                            }
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                          Secondary Point of Contact
                        </Form.Label>
                        <StyledSelect
                          value={formData.secondary_poc || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              secondary_poc: e.target.value || undefined,
                            })
                          }
                        >
                          <option value="">Select Secondary POC</option>
                          {organizationUsers.map((user: User) => (
                            <option key={user.id} value={user.id}>
                              {user.first_name} {user.last_name} ({user.username})
                            </option>
                          ))}
                        </StyledSelect>
                        {formData.secondary_poc && (
                          <div
                            style={{
                              marginTop: '0.5rem',
                              color: 'rgba(255, 255, 255, 0.7)',
                              fontSize: '0.875rem',
                            }}
                          >
                            <FaEnvelope size={12} style={{ marginRight: '0.5rem' }} />
                            {
                              organizationUsers.find((u: User) => u.id === formData.secondary_poc)
                                ?.email
                            }
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                          Primary Phone
                        </Form.Label>
                        <StyledFormControl
                          type="tel"
                          value={formData.primary_phone || ''}
                          onChange={(e) =>
                            setFormData({ ...formData, primary_phone: e.target.value })
                          }
                          placeholder="Enter primary phone"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                          Secondary Phone
                        </Form.Label>
                        <StyledFormControl
                          type="tel"
                          value={formData.secondary_phone || ''}
                          onChange={(e) =>
                            setFormData({ ...formData, secondary_phone: e.target.value })
                          }
                          placeholder="Enter secondary phone"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                          Created At
                        </Form.Label>
                        <StyledFormControl
                          type="text"
                          value={formatDate(organization.created_at)}
                          disabled
                          style={{ opacity: 0.6 }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                          Updated At
                        </Form.Label>
                        <StyledFormControl
                          type="text"
                          value={formatDate(organization.updated_at)}
                          disabled
                          style={{ opacity: 0.6 }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Form>
              )}
            </div>
          </SectionCard>
        </Col>
      </Row>
    </>
  )
}

export default OrganizationTab
